import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { themeService } from '../../services/themeService';
import { soundService, MUSIC_THEMES } from '../../services/soundService';
import { Language } from '../../services/languageService';
import { subscriptionService } from '../../services/subscriptionService';
import SubscriptionModal from '../SubscriptionModal';
import BackgroundCustomizer from '../BackgroundCustomizer';
import AvatarSelector from '../AvatarSelector';

type SettingsCategory = 'profile' | 'audio' | 'preferences' | 'background' | 'account';

interface SettingsPageProps {
  initialTab?: SettingsCategory;
}

const SettingsPageNew: React.FC<SettingsPageProps> = ({ initialTab = 'profile' }) => {
  const { profile, updateProfile, updateBackgroundSettings, getBackgroundSettings, user, isDemoMode } = useUser();
  const { language, setLanguage, t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState<SettingsCategory>(initialTab);
  const [playerName, setPlayerName] = useState(profile?.name || '');
  const [soundSettings, setSoundSettings] = useState(() => soundService.getSettings());
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(() => {
    return themeService.getCurrentTheme().id;
  });
  
  // Password change modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  
  // Delete account modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  
  // Background customizer modal state
  const [showBackgroundCustomizer, setShowBackgroundCustomizer] = useState(false);
  const [backgroundInitialType, setBackgroundInitialType] = useState<'preset' | 'custom_image' | 'gradient' | 'video' | 'solid' | undefined>(undefined);
  const [backgroundInitialParticle, setBackgroundInitialParticle] = useState<'none' | 'snow' | 'stars' | 'fireflies' | 'sakura' | 'embers' | undefined>(undefined);
  
  // Sync sound settings from service
  useEffect(() => {
    setSoundSettings(soundService.getSettings());
  }, []);
  
  // Update active category when initialTab changes
  useEffect(() => {
    if (initialTab) {
      setActiveCategory(initialTab);
    }
  }, [initialTab]);
  
  if (!profile) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  const handleSaveName = () => {
    if (playerName.trim()) {
      updateProfile({ name: playerName.trim() });
      showSuccessMessage();
    }
  };

  const handleAvatarSelect = (avatarPath: string) => {
    updateProfile({ avatar: avatarPath });
    showSuccessMessage();
  };

  const handleSoundToggle = (setting: keyof typeof soundSettings) => {
    const newValue = !soundSettings[setting];
    const newSettings = { ...soundSettings, [setting]: newValue };
    setSoundSettings(newSettings);
    soundService.updateSettings({ [setting]: newValue });
    showSuccessMessage();
  };
  
  const handleVolumeChange = (type: 'voiceVolume' | 'sfxVolume' | 'musicVolume', value: number) => {
    const newSettings = { ...soundSettings, [type]: value };
    setSoundSettings(newSettings);
    soundService.updateSettings({ [type]: value });
  };
  
  const testSound = (type: 'voice' | 'sfx') => {
    if (type === 'voice') {
      soundService.questComplete();
    } else {
      soundService.buttonClick();
    }
  };

  const handleMusicThemeChange = (themeId: string) => {
    soundService.setMusicTheme(themeId);
    setSoundSettings(soundService.getSettings());
    showSuccessMessage();
  };

  const showSuccessMessage = () => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    setPasswordChanging(true);
    
    try {
      const { authService } = await import('../../services/authService');
      await authService.changePassword(currentPassword, newPassword);
      
      // Success
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showSuccessMessage();
    } catch (error: any) {
      setPasswordError(error.message || 'Failed to change password');
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    
    // Get current user email
    const { authService } = await import('../../services/authService');
    const user = authService.getCurrentUser();
    
    if (!user || !user.email) {
      setDeleteError('Unable to verify user');
      return;
    }
    
    // Validation - email required
    if (!deleteConfirmEmail) {
      setDeleteError('Please confirm your email address');
      return;
    }
    
    if (deleteConfirmEmail.toLowerCase() !== user.email.toLowerCase()) {
      setDeleteError('Email does not match your account');
      return;
    }
    
    // For email/password users, require password
    const providerId = user.providerData[0]?.providerId;
    if (providerId === 'password' && !deleteConfirmPassword) {
      setDeleteError('Please enter your password');
      return;
    }
    
    setIsDeleting(true);
    
    try {
      await authService.deleteAccount(deleteConfirmPassword);
      
      // Success - account deleted, user will be automatically signed out
      // No need to navigate, Firebase will trigger auth state change
    } catch (error: any) {
      setDeleteError(error.message || 'Failed to delete account. Please try again or contact support.');
      setIsDeleting(false);
    }
  };

  const themes = themeService.getAllThemes();
  const accessibleThemes = profile ? subscriptionService.getAccessibleThemes(profile) : ['default'];

  const handleThemeSelect = (themeId: string) => {
    if (!profile) return;
    
    // Check if user can access this theme
    const accessCheck = subscriptionService.canAccessTheme(profile, themeId);
    if (!accessCheck.allowed) {
      // Show subscription modal instead of error
      setShowSubscriptionModal(true);
      return;
    }
    
    themeService.setTheme(themeId);
    setSelectedTheme(themeId);
    showSuccessMessage();
  };

  // Category navigation items
  const categories = [
    { id: 'profile' as SettingsCategory, label: t.settings.profile, iconPath: '/icons/icon-settings-profile.png', color: 'blue' },
    { id: 'audio' as SettingsCategory, label: t.settings.audio, iconPath: '/icons/icon-settings-sound.png', color: 'purple' },
    { id: 'preferences' as SettingsCategory, label: t.settings.preferences || 'Preferences', iconPath: '/icons/icon-settings-preferences.png', color: 'green' },
    { id: 'background' as SettingsCategory, label: 'Background', iconPath: '/icons/icon-settings-background.png', color: 'shadow' },
    { id: 'account' as SettingsCategory, label: t.settings.account || 'Account', iconPath: '/icons/icon-settings-account.png', color: 'red' },
  ];

  return (
    <div className="animate-fade-in max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-8">
      {/* Success Message */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-gradient-to-r from-system-blue to-system-purple text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg shadow-lg animate-slide-in z-50">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="text-base sm:text-xl">✓</span>
            <span className="font-orbitron font-bold text-sm sm:text-base">{t.settings.settingsSaved}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-system-blue rounded-full animate-pulse"></div>
          <span className="font-orbitron text-lg sm:text-2xl tracking-wider text-system-blue uppercase">
            ［ {t.settings.title} ］
          </span>
        </div>
        <p className="text-xs sm:text-sm text-gray-400 font-rajdhani">
          {t.settings.subtitle}
        </p>
      </div>

      {/* Two-column layout */}
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        {/* Left Sidebar - Categories */}
        <div className="lg:w-64 flex-shrink-0">
          <div 
            className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-2 sm:p-3 sticky top-4 prevent-drag"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div className="space-y-1">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-rajdhani font-semibold text-sm sm:text-base transition-all ${
                    activeCategory === category.id
                      ? 'bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 text-system-blue border-2 border-t-slate-400/40 border-l-slate-400/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 shadow-neon-blue relative'
                      : 'text-gray-400 hover:text-white hover:bg-slate-800/30 border-2 border-transparent hover:border-slate-600/30'
                  }`}
                  style={activeCategory === category.id ? {
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), 0 1px 4px rgba(59, 130, 246, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -1px 2px rgba(0, 0, 0, 0.3)'
                  } : {}}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <img 
                      src={category.iconPath} 
                      alt={category.label}
                      className={`${activeCategory === category.id ? 'w-6 h-6 sm:w-7 sm:h-7' : 'w-5 h-5 sm:w-6 sm:h-6 opacity-90'} object-contain`}
                      style={{ 
                        filter: activeCategory === category.id 
                          ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.9)) brightness(1.2) contrast(1.1)'
                          : 'drop-shadow(0 0 3px rgba(59, 130, 246, 0.5)) brightness(1.05)'
                      }}
                    />
                    <span>{category.label}</span>
                  </div>
                  {activeCategory === category.id && (
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-lg pointer-events-none" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 min-w-0">
          <div className="space-y-4 sm:space-y-6">
            {/* Profile Section */}
            {activeCategory === 'profile' && (
              <div className="space-y-4">
                {/* Profile Info */}
                <div 
                  className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6 prevent-drag"
                  style={{
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <h3 className="text-lg sm:text-xl font-bold font-orbitron text-system-blue mb-4 flex items-center gap-2">
                    <span className="text-xl">👤</span>
                    <span>{t.settings.profile}</span>
                  </h3>
                  
                  {/* Player Name */}
                  <div className="mb-4 sm:mb-6">
                    <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase mb-2">
                      {t.settings.playerName}
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <input
                        type="text"
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                        className="flex-1 bg-system-dark border-2 border-system-blue/30 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white font-orbitron text-sm sm:text-base focus:border-system-blue outline-none transition-all"
                        placeholder={t.settings.namePlaceholder}
                      />
                      <button
                        onClick={handleSaveName}
                        className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-system-blue to-system-purple rounded-xl font-orbitron font-bold tracking-wider transition-all hover:scale-105 text-xs sm:text-sm whitespace-nowrap relative overflow-hidden group"
                        style={{
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), 0 1px 4px rgba(59, 130, 246, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.2), inset 0 -1px 2px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
                        <span className="relative">{t.settings.save}</span>
                      </button>
                    </div>
                  </div>

                  {/* Stats Display */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div 
                      className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-4"
                      style={{
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      <div className="text-gray-400 font-rajdhani uppercase text-xs sm:text-sm mb-1 sm:mb-2">{t.common.level}</div>
                      <div className="text-xl sm:text-2xl lg:text-3xl font-bold font-orbitron text-system-blue">{profile.level}</div>
                    </div>
                    <div 
                      className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-4"
                      style={{
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      <div className="text-gray-400 font-rajdhani uppercase text-xs sm:text-sm mb-1 sm:mb-2">{t.settings.title_label}</div>
                      <div className="text-xs sm:text-sm lg:text-base font-bold font-orbitron text-system-purple truncate">{profile.currentTitle}</div>
                    </div>
                  </div>

                  {/* Avatar Selection */}
                  <AvatarSelector
                    selectedAvatar={profile.avatar}
                    onAvatarSelect={handleAvatarSelect}
                    userProfile={profile}
                    className="mb-6"
                  />
                </div>
              </div>
            )}

            {/* Audio Section */}
            {activeCategory === 'audio' && (
              <div 
                className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-6 prevent-drag"
                style={{
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                }}
              >
                <h3 className="text-lg sm:text-xl font-bold font-orbitron text-system-purple mb-4 sm:mb-6 flex items-center gap-2">
                  <span className="text-xl">🔊</span>
                  <span>{t.settings.audio}</span>
                </h3>

                <div className="space-y-3 sm:space-y-4">
                  {/* Master Sound */}
                  <div 
                    className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
                    style={{
                      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <div className="flex-1 pr-2 sm:pr-3">
                      <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">{t.settings.masterAudio}</div>
                      <div className="text-xs sm:text-sm text-gray-400 font-rajdhani leading-relaxed">{t.settings.masterAudioDesc}</div>
                    </div>
                    <button
                      onClick={() => handleSoundToggle('enabled')}
                      className={`relative w-16 h-8 rounded-full transition-all touch-manipulation ${
                        soundSettings.enabled ? 'bg-system-blue' : 'bg-gray-600'
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-lg ${
                          soundSettings.enabled ? 'left-9' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Voice Lines */}
                  <div 
                    className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
                    style={{
                      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <div className="flex-1 pr-2 sm:pr-3">
                      <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">{t.settings.systemVoice}</div>
                      <div className="text-xs sm:text-sm text-gray-400 font-rajdhani leading-relaxed">{t.settings.systemVoiceDesc}</div>
                    </div>
                    <button
                      onClick={() => handleSoundToggle('voiceEnabled')}
                      disabled={!soundSettings.enabled}
                      className={`relative w-16 h-8 rounded-full transition-all touch-manipulation ${
                        soundSettings.voiceEnabled && soundSettings.enabled ? 'bg-system-purple' : 'bg-gray-600'
                      } ${!soundSettings.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div
                        className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-lg ${
                          soundSettings.voiceEnabled ? 'left-9' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Voice Volume */}
                  <div 
                    className="p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
                    style={{
                      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2 sm:gap-0">
                      <div className="flex-1">
                        <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">{t.settings.voiceVolume}</div>
                        <div className="text-xs sm:text-sm text-gray-400 font-rajdhani leading-relaxed">{t.settings.voiceVolumeDesc}</div>
                      </div>
                      <div className="text-lg sm:text-xl font-black font-orbitron text-system-purple bg-system-darker px-3 py-1 rounded-lg border border-system-purple/30">
                        {Math.round(soundSettings.voiceVolume * 100)}%
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={soundSettings.voiceVolume}
                        onChange={(e) => handleVolumeChange('voiceVolume', parseFloat(e.target.value))}
                        disabled={!soundSettings.enabled || !soundSettings.voiceEnabled}
                        className="w-full h-3 sm:h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-system-purple disabled:opacity-50 touch-manipulation slider-thumb"
                        style={{
                          background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${soundSettings.voiceVolume * 100}%, #374151 ${soundSettings.voiceVolume * 100}%, #374151 100%)`
                        }}
                      />
                    </div>
                  </div>

                  {/* SFX Volume */}
                  <div 
                    className="p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
                    style={{
                      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2 sm:gap-0">
                      <div className="flex-1">
                        <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">{t.settings.sfxVolume}</div>
                        <div className="text-xs sm:text-sm text-gray-400 font-rajdhani leading-relaxed">{t.settings.sfxVolumeDesc}</div>
                      </div>
                      <div className="text-lg sm:text-xl font-black font-orbitron text-system-purple bg-system-darker px-3 py-1 rounded-lg border border-system-purple/30">
                        {Math.round(soundSettings.sfxVolume * 100)}%
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={soundSettings.sfxVolume}
                        onChange={(e) => handleVolumeChange('sfxVolume', parseFloat(e.target.value))}
                        disabled={!soundSettings.enabled}
                        className="w-full h-3 sm:h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-system-purple disabled:opacity-50 touch-manipulation slider-thumb"
                        style={{
                          background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${soundSettings.sfxVolume * 100}%, #374151 ${soundSettings.sfxVolume * 100}%, #374151 100%)`
                        }}
                      />
                    </div>
                  </div>

                  {/* Background Music */}
                  <div 
                    className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
                    style={{
                      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <div className="flex-1 pr-2 sm:pr-3">
                      <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">{t.settings.backgroundMusic}</div>
                      <div className="text-xs sm:text-sm text-gray-400 font-rajdhani leading-relaxed">{t.settings.backgroundMusicDesc}</div>
                    </div>
                    <button
                      onClick={() => handleSoundToggle('musicEnabled')}
                      disabled={!soundSettings.enabled}
                      className={`relative w-16 h-8 rounded-full transition-all touch-manipulation ${
                        soundSettings.musicEnabled && soundSettings.enabled ? 'bg-system-purple' : 'bg-gray-600'
                      } ${!soundSettings.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div
                        className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-lg ${
                          soundSettings.musicEnabled ? 'left-9' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Music Theme */}
                  {soundSettings.musicEnabled && (
                    <div 
                      className="p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl animate-fade-in"
                      style={{
                        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <div className="mb-3 sm:mb-4">
                        <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">{t.settings.musicTheme}</div>
                        <div className="text-xs sm:text-sm text-gray-400 font-rajdhani leading-relaxed">{t.settings.musicThemeDesc}</div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {MUSIC_THEMES.map((theme) => (
                          <button
                            key={theme.id}
                            onClick={() => handleMusicThemeChange(theme.id)}
                            disabled={!soundSettings.enabled}
                            className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-all touch-manipulation ${
                              soundSettings.musicTheme === theme.id
                                ? 'border-system-purple bg-system-purple/20 shadow-neon-purple'
                                : 'border-gray-700 hover:border-system-purple/50'
                            } ${!soundSettings.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <span className="text-base sm:text-lg">{theme.id === 'none' ? '🔇' : '🎵'}</span>
                                <span className="font-orbitron font-bold text-white text-sm sm:text-base">{theme.name}</span>
                              </div>
                              {soundSettings.musicTheme === theme.id && (
                                <span className="text-system-purple text-lg">✓</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Music Volume */}
                  <div 
                    className="p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
                    style={{
                      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2 sm:gap-0">
                      <div className="flex-1">
                        <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">{t.settings.musicVolume}</div>
                        <div className="text-xs sm:text-sm text-gray-400 font-rajdhani leading-relaxed">{t.settings.musicVolumeDesc}</div>
                      </div>
                      <div className="text-lg sm:text-xl font-black font-orbitron text-system-purple bg-system-darker px-3 py-1 rounded-lg border border-system-purple/30">
                        {Math.round(soundSettings.musicVolume * 100)}%
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={soundSettings.musicVolume}
                        onChange={(e) => handleVolumeChange('musicVolume', parseFloat(e.target.value))}
                        disabled={!soundSettings.enabled || !soundSettings.musicEnabled}
                        className="w-full h-3 sm:h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-system-purple disabled:opacity-50 touch-manipulation slider-thumb"
                        style={{
                          background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${soundSettings.musicVolume * 100}%, #374151 ${soundSettings.musicVolume * 100}%, #374151 100%)`
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Section */}
            {activeCategory === 'preferences' && (
              <div className="space-y-3 sm:space-y-4">
                {/* Language */}
                <div 
                  className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-6 prevent-drag"
                  style={{
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <h3 className="text-lg sm:text-xl font-bold font-orbitron text-system-blue mb-3 sm:mb-4 flex items-center gap-2">
                    <span className="text-lg sm:text-xl">🌐</span>
                    <span>{t.settings.language}</span>
                  </h3>

                  <p className="text-xs sm:text-sm text-gray-400 font-rajdhani mb-3 sm:mb-4 leading-relaxed">
                    {t.settings.languageDesc}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    <button
                      onClick={() => {
                        setLanguage('en');
                        showSuccessMessage();
                      }}
                      className={`p-3 sm:p-4 rounded-2xl border-2 transition-all touch-manipulation relative overflow-hidden ${
                        language === 'en'
                          ? 'border-t-blue-500/60 border-l-blue-500/60 border-r-blue-600/80 border-b-blue-700/80 bg-gradient-to-b from-blue-900/40 via-blue-950/40 to-blue-950/60 shadow-lg shadow-blue-500/50'
                          : 'border-t-slate-500/60 border-l-slate-500/60 border-r-slate-700/80 border-b-slate-800/80 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80'
                      }`}
                      style={language === 'en' ? {
                        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.5), 0 2px 8px rgba(59, 130, 246, 0.3), inset 0 2px 4px rgba(59, 130, 246, 0.2), inset 0 -3px 8px rgba(30, 58, 138, 0.5)'
                      } : {
                        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      {language === 'en' && (
                        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
                      )}
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-2xl sm:text-3xl">🇬🇧</span>
                        <div className="text-left flex-1">
                          <div className="font-orbitron font-bold text-white text-sm sm:text-base">English</div>
                          <div className="text-xs text-gray-400 font-rajdhani">{t.settings.english}</div>
                        </div>
                          {language === 'en' && (
                          <span className="ml-auto text-system-blue text-lg">✓</span>
                        )}
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setLanguage('ru');
                        showSuccessMessage();
                      }}
                      className={`p-3 sm:p-4 rounded-2xl border-2 transition-all touch-manipulation relative overflow-hidden ${
                        language === 'ru'
                          ? 'border-t-blue-500/60 border-l-blue-500/60 border-r-blue-600/80 border-b-blue-700/80 bg-gradient-to-b from-blue-900/40 via-blue-950/40 to-blue-950/60 shadow-lg shadow-blue-500/50'
                          : 'border-t-slate-500/60 border-l-slate-500/60 border-r-slate-700/80 border-b-slate-800/80 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80'
                      }`}
                      style={language === 'ru' ? {
                        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.5), 0 2px 8px rgba(59, 130, 246, 0.3), inset 0 2px 4px rgba(59, 130, 246, 0.2), inset 0 -3px 8px rgba(30, 58, 138, 0.5)'
                      } : {
                        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      {language === 'ru' && (
                        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
                      )}
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-2xl sm:text-3xl">🇷🇺</span>
                        <div className="text-left flex-1">
                          <div className="font-orbitron font-bold text-white text-sm sm:text-base">Русский</div>
                          <div className="text-xs text-gray-400 font-rajdhani">{t.settings.russian}</div>
                        </div>
                        {language === 'ru' && (
                          <span className="ml-auto text-system-blue text-lg">✓</span>
                        )}
                      </div>
                    </button>
                  </div>
                </div>

                {/* Tutorial */}
                <div 
                  className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-6 prevent-drag"
                  style={{
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <h3 className="text-lg sm:text-xl font-bold font-orbitron text-system-blue mb-3 sm:mb-4 flex items-center gap-2">
                    <span className="text-lg sm:text-xl">📖</span>
                    <span>{t.settings.replayTutorial}</span>
                  </h3>

                  <p className="text-xs sm:text-sm text-gray-400 font-rajdhani mb-3 sm:mb-4 leading-relaxed">
                    {t.settings.replayTutorialDesc}
                  </p>

                  <button
                    onClick={() => {
                      localStorage.removeItem('onboarding_completed');
                      window.location.reload();
                    }}
                    className="px-4 sm:px-6 py-2.5 sm:py-3 font-orbitron font-bold w-full text-sm sm:text-base touch-manipulation bg-gradient-to-r from-system-blue to-system-purple rounded-2xl relative overflow-hidden group"
                    style={{
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), 0 1px 4px rgba(59, 130, 246, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.2), inset 0 -1px 2px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      <span className="text-base sm:text-lg">🎓</span>
                      <span className="tracking-wider sm:tracking-widest uppercase">{t.settings.replayTutorial}</span>
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Background Section */}
            {activeCategory === 'background' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Header Section */}
                <div 
                  className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-6 prevent-drag"
                  style={{
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-2xl font-bold font-orbitron text-transparent bg-clip-text bg-gradient-to-r from-system-blue to-system-blue mb-2 flex items-center gap-2">
                        <span className="text-lg sm:text-2xl">⚔️</span>
                        <span>Shadow Domain Configuration</span>
                  </h3>
                      <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">
                        Manifest your Hunter's personal realm with custom gates, mana auras, and atmospheric effects
                  </p>
                    </div>
                  <button
                      onClick={() => {
                        setBackgroundInitialType(undefined);
                        setBackgroundInitialParticle(undefined);
                        setShowBackgroundCustomizer(true);
                      }}
                      className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-system-blue to-system-purple rounded-2xl transition-all font-semibold flex items-center justify-center gap-2 group text-sm sm:text-base touch-manipulation w-full sm:w-auto relative overflow-hidden hover:scale-105 active:scale-95"
                      style={{
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), 0 1px 4px rgba(59, 130, 246, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.2), inset 0 -1px 2px rgba(0, 0, 0, 0.2)'
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
                      <span className="relative flex items-center justify-center gap-2">
                        <span className="text-base sm:text-lg group-hover:scale-110 transition-transform">✨</span>
                        <span>Customize Realm</span>
                      </span>
                  </button>
                  </div>
                </div>

                {/* Realm Types Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {/* Gate Realms */}
                  <div 
                    className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-5 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80 transition-all group cursor-pointer touch-manipulation prevent-drag" 
                    style={{
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                    }}
                    onClick={() => {
                    setBackgroundInitialType('preset');
                    setBackgroundInitialParticle(undefined);
                    setShowBackgroundCustomizer(true);
                  }}>
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-system-blue/20 to-system-blue/10 flex items-center justify-center text-xl sm:text-2xl group-hover:scale-110 transition-transform">
                        🌌
                      </div>
                      <div>
                        <h4 className="font-orbitron font-bold text-system-blue group-hover:text-system-blue transition-colors text-sm sm:text-base">Gate Realms</h4>
                        <p className="text-xs text-gray-500">Preset Dimensions</p>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">
                      Access pre-configured dimensional gates inspired by legendary dungeons
                    </p>
                    <div className="mt-2 sm:mt-3 flex items-center gap-2 text-xs text-system-blue">
                      <span className="font-semibold">6 Premium Realms</span>
                      <span className="text-gray-600">•</span>
                      <span className="text-gray-500">Instant activation</span>
                    </div>
                  </div>

                  {/* Custom Gates */}
                  <div 
                    className={`bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-5 transition-all group cursor-pointer touch-manipulation prevent-drag ${
                      isDemoMode 
                        ? 'opacity-50' 
                        : 'hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80'
                    }`} 
                    style={{
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                    }}
                    onClick={() => {
                    if (isDemoMode) {
                      // Show signup prompt for locked features in demo mode
                      alert('🔒 Sign up to unlock custom background uploads and save your progress!');
                      return;
                    }
                    setBackgroundInitialType('custom_image');
                    setBackgroundInitialParticle(undefined);
                    setShowBackgroundCustomizer(true);
                  }}>
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-system-blue/20 to-system-blue/10 flex items-center justify-center text-xl sm:text-2xl group-hover:scale-110 transition-transform">
                        🎭
                      </div>
                      <div>
                        <h4 className={`font-orbitron font-bold transition-colors text-sm sm:text-base ${
                          isDemoMode 
                            ? 'text-gray-400' 
                            : 'text-system-blue group-hover:text-system-blue'
                        }`}>
                          Custom Gates
                          {isDemoMode && <span className="ml-2 text-xs">🔒</span>}
                        </h4>
                        <p className="text-xs text-gray-500">Image & Video Upload</p>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">
                      Summon your own dimensional gates using custom images or animated realms
                    </p>
                    <div className="mt-2 sm:mt-3 flex items-center gap-2 text-xs text-system-blue">
                      <span className="font-semibold">10MB Images</span>
                      <span className="text-gray-600">•</span>
                      <span className="text-gray-500">50MB Videos</span>
                    </div>
                  </div>

                  {/* Mana Auras */}
                  <div 
                    className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-5 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80 transition-all group cursor-pointer touch-manipulation prevent-drag" 
                    style={{
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                    }}
                    onClick={() => {
                    setBackgroundInitialType('gradient');
                    setBackgroundInitialParticle(undefined);
                    setShowBackgroundCustomizer(true);
                  }}>
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-system-blue/20 to-system-blue/10 flex items-center justify-center text-xl sm:text-2xl group-hover:scale-110 transition-transform">
                        💫
                      </div>
                      <div>
                        <h4 className="font-orbitron font-bold text-system-blue group-hover:text-system-blue transition-colors text-sm sm:text-base">Mana Auras</h4>
                        <p className="text-xs text-gray-500">Gradient Energy</p>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">
                      Configure flowing mana energy with customizable color gradients
                    </p>
                    <div className="mt-2 sm:mt-3 flex items-center gap-2 text-xs text-system-blue">
                      <span className="font-semibold">Unlimited Colors</span>
                      <span className="text-gray-600">•</span>
                      <span className="text-gray-500">360° flow</span>
                    </div>
                  </div>
                </div>

                {/* Atmospheric Effects */}
                <div 
                  className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-6 prevent-drag"
                  style={{
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <h4 className="text-lg sm:text-xl font-bold font-orbitron text-system-blue mb-3 sm:mb-4 flex items-center gap-2">
                    <span className="text-lg sm:text-xl">✨</span>
                    <span>Atmospheric Mana Particles</span>
                  </h4>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
                    {[
                      { icon: '❄️', name: 'Snow', desc: 'Gentle snowflakes falling', effect: 'snow' as const },
                      { icon: '✨', name: 'Sparkles', desc: 'Twinkling magical sparkles', effect: 'stars' as const },
                      { icon: '🌟', name: 'Starlight', desc: 'Glowing starlight particles', effect: 'fireflies' as const },
                      { icon: '🌸', name: 'Petals', desc: 'Floating cherry blossoms', effect: 'sakura' as const },
                      { icon: '🔥', name: 'Embers', desc: 'Rising ember particles', effect: 'embers' as const },
                    ].map((particle) => {
                      const currentSettings = getBackgroundSettings();
                      const isActive = currentSettings.particleEffect === particle.effect;
                      
                      return (
                        <div
                          key={particle.name}
                          className={`rounded-2xl p-2 sm:p-3 text-center transition-all cursor-pointer group border-2 touch-manipulation ${
                            isActive 
                              ? 'border-t-blue-500/60 border-l-blue-500/60 border-r-blue-600/80 border-b-blue-700/80 bg-gradient-to-b from-blue-900/40 via-blue-950/40 to-blue-950/60' 
                              : 'border-t-slate-500/60 border-l-slate-500/60 border-r-slate-700/80 border-b-slate-800/80 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80'
                          }`}
                          style={isActive ? {
                            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.5), 0 2px 8px rgba(59, 130, 246, 0.3), inset 0 2px 4px rgba(59, 130, 246, 0.2), inset 0 -3px 8px rgba(30, 58, 138, 0.5)'
                          } : {
                            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                          }}
                          onClick={async () => {
                            // Play feedback
                            soundService.buttonClick();
                            
                            // Update particle effect directly
                            const newSettings = {
                              ...currentSettings,
                              particleEffect: particle.effect,
                            };
                            
                            await updateBackgroundSettings(newSettings);
                            
                            // Show success message
                            setShowSuccess(true);
                            setTimeout(() => setShowSuccess(false), 2000);
                          }}
                        >
                          <div className={`text-2xl sm:text-3xl mb-1 sm:mb-2 group-hover:scale-110 transition-transform ${isActive ? 'animate-pulse' : ''}`}>
                            {particle.icon}
                          </div>
                          <div className={`text-xs font-semibold transition-colors ${
                            isActive ? 'text-system-blue' : 'text-gray-300 group-hover:text-system-blue'
                          }`}>
                            {particle.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 leading-tight">{particle.desc}</div>
                          {isActive && (
                            <div className="mt-1 sm:mt-2 text-xs text-system-blue font-semibold">
                              ✓ Active
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Particle Density Control */}
                  {(() => {
                    const currentSettings = getBackgroundSettings();
                    const hasActiveParticles = currentSettings.particleEffect !== 'none';
                    
                    if (!hasActiveParticles) return null;
                    
                    return (
                      <div className="mt-4 p-3 bg-gray-800/30 rounded-lg border border-gray-700/50">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Particle Density: {currentSettings.particleIntensity}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={currentSettings.particleIntensity}
                          onChange={async (e) => {
                            const newSettings = {
                              ...currentSettings,
                              particleIntensity: parseInt(e.target.value),
                            };
                            await updateBackgroundSettings(newSettings);
                            soundService.buttonClick();
                          }}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                          style={{
                            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${currentSettings.particleIntensity}%, #374151 ${currentSettings.particleIntensity}%, #374151 100%)`
                          }}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Minimal</span>
                          <span>Intense</span>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Quick Actions */}
                  <div className="mt-3 sm:mt-4 flex justify-center">
                    <button
                      onClick={async () => {
                        const currentSettings = getBackgroundSettings();
                        const newSettings = {
                          ...currentSettings,
                          particleEffect: 'none' as const,
                        };
                        await updateBackgroundSettings(newSettings);
                        soundService.buttonClick();
                        setShowSuccess(true);
                        setTimeout(() => setShowSuccess(false), 2000);
                      }}
                      className="text-xs text-gray-400 hover:text-system-blue transition-colors underline touch-manipulation py-1"
                    >
                      Clear all particles
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Account Section */}
            {activeCategory === 'account' && (
              <div className="space-y-3 sm:space-y-4">
                {/* Subscription Management */}
                <div 
                  className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-6 prevent-drag"
                  style={{
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <h3 className="text-lg sm:text-xl font-bold font-orbitron text-system-purple mb-3 sm:mb-4 flex items-center gap-2">
                    <span className="text-lg sm:text-xl">💎</span>
                    <span>SUBSCRIPTION</span>
                  </h3>
                  
                  {profile.subscription && (
                    <div className="space-y-4">
                      {/* Current Plan */}
                      <div 
                        className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-4"
                        style={{
                          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                        }}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 mb-2 sm:mb-3">
                          <div>
                            <p className="text-xs text-gray-400 font-rajdhani uppercase">Current Plan</p>
                            <p className="text-lg sm:text-xl font-bold font-orbitron text-system-blue">
                              {subscriptionService.getTierDisplayName(profile.subscription.tier)}
                            </p>
                          </div>
                          {profile.subscription.tier === 'trial' && (
                            <div className="px-2 sm:px-3 py-1 bg-orange-500/20 border border-orange-500 rounded text-orange-400 text-xs font-bold self-start">
                              ⏰ {subscriptionService.getTrialDaysRemaining(profile.subscription)} DAYS LEFT
                            </div>
                          )}
                          {(profile.subscription.tier === 'e_rank' || profile.subscription.tier === 's_rank') && (
                            <>
                              {profile.subscription.cancelAtPeriodEnd ? (
                                <div className="px-2 sm:px-3 py-1 bg-orange-500/20 border border-orange-500 rounded text-orange-400 text-xs font-bold self-start">
                                  ⚠️ CANCELS {new Date(profile.subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </div>
                              ) : (
                                <div className="px-2 sm:px-3 py-1 bg-green-500/20 border border-green-500 rounded text-green-400 text-xs font-bold self-start">
                                  ✅ ACTIVE
                                </div>
                              )}
                            </>
                          )}
                          {profile.subscription.tier === 'expired' && (
                            <div className="px-2 sm:px-3 py-1 bg-red-500/20 border border-red-500 rounded text-red-400 text-xs font-bold self-start">
                              ❌ EXPIRED
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Cancellation Warning */}
                      {profile.subscription.cancelAtPeriodEnd && (profile.subscription.tier === 'e_rank' || profile.subscription.tier === 's_rank') && (
                        <div className="bg-orange-950/30 border-2 border-orange-500/50 rounded-lg p-3 sm:p-4">
                          <div className="flex items-start gap-2 sm:gap-3">
                            <span className="text-xl sm:text-2xl flex-shrink-0">⚠️</span>
                            <div>
                              <p className="font-orbitron font-bold text-orange-400 text-xs sm:text-sm mb-1">
                                SUBSCRIPTION CANCELLED
                              </p>
                              <p className="text-orange-200 text-xs font-rajdhani mb-2 leading-relaxed">
                                Your subscription will end on <strong>{new Date(profile.subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>. 
                                You can continue using all features until then.
                              </p>
                              <p className="text-orange-300/80 text-xs font-rajdhani leading-relaxed">
                                💡 Want to keep your progress? You can reactivate anytime before the end date!
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* AI Usage Stats */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        <div 
                          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-2.5 sm:p-3"
                          style={{
                            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                          }}
                        >
                          <p className="text-xs text-gray-400 font-rajdhani uppercase mb-1">AI Quest Generations</p>
                          <p className="text-base sm:text-lg font-bold font-orbitron">
                            {(() => {
                              const questCheck = subscriptionService.canUseAIQuestGeneration(profile);
                              const features = subscriptionService.getCurrentFeatures(profile);
                              if (features.aiQuestGenerationsPerMonth === 'unlimited') {
                                return '♾️ Unlimited';
                              }
                              const used = features.aiQuestGenerationsPerMonth - (questCheck.remaining as number);
                              return `${used} / ${features.aiQuestGenerationsPerMonth}`;
                            })()}
                          </p>
                        </div>
                        
                        <div 
                          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-2.5 sm:p-3"
                          style={{
                            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                          }}
                        >
                          <p className="text-xs text-gray-400 font-rajdhani uppercase mb-1">AI Program Generations</p>
                          <p className="text-base sm:text-lg font-bold font-orbitron">
                            {(() => {
                              const programCheck = subscriptionService.canUseAIProgramGeneration(profile);
                              const features = subscriptionService.getCurrentFeatures(profile);
                              if (features.aiProgramGenerationsPerMonth === 'unlimited') {
                                return '♾️ Unlimited';
                              }
                              const used = features.aiProgramGenerationsPerMonth - (programCheck.remaining as number);
                              return `${used} / ${features.aiProgramGenerationsPerMonth}`;
                            })()}
                          </p>
                        </div>
                      </div>
                      
                      {/* Features Summary */}
                      <div 
                        className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-4"
                        style={{
                          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                        }}
                      >
                        <p className="text-xs text-gray-400 font-rajdhani uppercase mb-2 sm:mb-3">Your Features</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 text-xs sm:text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-system-blue">✓</span>
                            <span className="text-gray-300">
                              {subscriptionService.getCurrentFeatures(profile).maxActivePassiveSkills === 'unlimited' 
                                ? 'Unlimited' 
                                : subscriptionService.getCurrentFeatures(profile).maxActivePassiveSkills} Passive Skills
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-system-blue">✓</span>
                            <span className="text-gray-300">
                              {subscriptionService.getCurrentFeatures(profile).historyRetentionDays === 'unlimited' 
                                ? 'Lifetime' 
                                : `${subscriptionService.getCurrentFeatures(profile).historyRetentionDays} days`} History
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-system-blue">✓</span>
                            <span className="text-gray-300">
                              {subscriptionService.getCurrentFeatures(profile).availableThemes === 'all' 
                                ? 'All' 
                                : subscriptionService.getCurrentFeatures(profile).availableThemes} Premium Themes
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-system-blue">✓</span>
                            <span className="text-gray-300">
                              {subscriptionService.getCurrentFeatures(profile).supportPriority === 'vip' 
                                ? 'VIP' 
                                : subscriptionService.getCurrentFeatures(profile).supportPriority === 'priority' 
                                ? 'Priority' 
                                : 'Community'} Support
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <button
                        onClick={() => setShowSubscriptionModal(true)}
                        className="w-full py-2.5 sm:py-3 font-orbitron font-bold text-sm sm:text-base touch-manipulation relative overflow-hidden group bg-gradient-to-r from-system-purple to-pink-600 rounded-2xl hover:from-purple-500 hover:to-pink-500 hover:scale-105 active:scale-95"
                        style={{
                          boxShadow: '0 4px 12px rgba(147, 51, 234, 0.4), 0 1px 4px rgba(219, 39, 119, 0.2), inset 0 1px 2px rgba(255, 100, 255, 0.2), inset 0 -1px 2px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
                        <span className="relative">{profile.subscription.tier === 'trial' || profile.subscription.tier === 'expired' 
                          ? '⚔️ UPGRADE NOW' 
                          : '⚙️ MANAGE SUBSCRIPTION'}</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Privacy & Security */}
                <div 
                  className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-6 prevent-drag"
                  style={{
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <h3 className="text-lg sm:text-xl font-bold font-orbitron text-system-blue mb-3 sm:mb-4 flex items-center gap-2">
                    <span className="text-lg sm:text-xl">🔐</span>
                    <span>Privacy & Security</span>
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <button 
                      onClick={() => setShowPasswordModal(true)}
                      className="w-full p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl text-left hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80 transition-all touch-manipulation"
                      style={{
                        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">Change Password</div>
                      <div className="text-xs text-gray-400 font-rajdhani">Update your account password</div>
                    </button>
                    <button 
                      className="w-full p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl text-left transition-all touch-manipulation"
                      style={{
                        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">Two-Factor Authentication</div>
                      <div className="text-xs text-gray-400 font-rajdhani">🚧 Coming Soon</div>
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div 
                  className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-800/70 rounded-3xl p-3 sm:p-6 prevent-drag"
                  style={{
                    boxShadow: '0 8px 24px rgba(239, 68, 68, 0.5), 0 2px 8px rgba(239, 68, 68, 0.3), inset 0 2px 4px rgba(255, 100, 100, 0.1), inset 0 -3px 8px rgba(153, 27, 27, 0.5)'
                  }}
                >
                  <h3 className="text-lg sm:text-xl font-bold font-orbitron text-red-400 mb-3 sm:mb-4 flex items-center gap-2">
                    <span className="text-lg sm:text-xl">⚠️</span>
                    <span>Danger Zone</span>
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div 
                      className="p-3 sm:p-4 bg-gradient-to-b from-red-950/30 via-red-900/20 to-red-950/30 backdrop-blur-sm border-t-2 border-l-2 border-red-900/60 border-r-2 border-b-2 border-r-red-950/80 border-b-red-950/80 rounded-3xl"
                      style={{
                        boxShadow: '0 6px 18px rgba(153, 27, 27, 0.5), 0 2px 6px rgba(153, 27, 27, 0.3), inset 0 1px 3px rgba(255, 100, 100, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <div className="font-orbitron font-bold text-white mb-1 text-sm sm:text-base">Export Data</div>
                      <div className="text-xs text-gray-400 font-rajdhani mb-2 sm:mb-3 leading-relaxed">Download all your quest data and progress</div>
                      <button className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 text-white rounded-2xl font-orbitron text-xs sm:text-sm transition-all touch-manipulation border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70 hover:scale-105 active:scale-95"
                        style={{
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                        }}
                      >
                        Export Data
                      </button>
                    </div>
                    <div 
                      className="p-3 sm:p-4 bg-gradient-to-b from-red-950/30 via-red-900/20 to-red-950/30 backdrop-blur-sm border-t-2 border-l-2 border-red-900/60 border-r-2 border-b-2 border-r-red-950/80 border-b-red-950/80 rounded-3xl"
                      style={{
                        boxShadow: '0 6px 18px rgba(153, 27, 27, 0.5), 0 2px 6px rgba(153, 27, 27, 0.3), inset 0 1px 3px rgba(255, 100, 100, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <div className="font-orbitron font-bold text-red-400 mb-1 text-sm sm:text-base">Delete Account</div>
                      <div className="text-xs text-gray-400 font-rajdhani mb-2 sm:mb-3 leading-relaxed">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </div>
                      <button 
                        onClick={() => setShowDeleteModal(true)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-b from-red-700/60 via-red-800/60 to-red-900/60 hover:from-red-600/70 hover:via-red-700/70 hover:to-red-800/70 text-white rounded-2xl font-orbitron text-xs sm:text-sm font-bold transition-all touch-manipulation border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-800/70 border-b-red-900/70 hover:scale-105 active:scale-95"
                        style={{
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4), 0 1px 4px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 100, 100, 0.1), inset 0 -2px 4px rgba(153, 27, 27, 0.4)'
                        }}
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center p-3 sm:p-4 z-[9999] animate-fade-in modal-backdrop">
          <div 
            className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl shadow-2xl max-w-xs sm:max-w-md w-full max-h-[70vh] sm:max-h-[75vh] overflow-y-auto animate-scale-in"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div 
              className="sticky top-0 border-b border-b-slate-600/30 p-3 sm:p-4 flex items-center justify-between z-10"
            >
              <h3 className="text-base sm:text-lg font-orbitron font-black text-system-blue uppercase tracking-wider">
                Change Password
              </h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordError('');
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Close"
              >
                <span className="text-lg text-gray-400">✕</span>
              </button>
            </div>

            <div className="p-2 sm:p-3 space-y-2 sm:space-y-3">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-rajdhani text-gray-400 uppercase mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl px-4 py-3 text-white font-orbitron focus:border-t-blue-400/70 focus:border-l-blue-400/70 focus:border-r-blue-600/80 focus:border-b-blue-700/80 outline-none transition-all"
                  style={{
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                  }}
                  placeholder="Enter current password"
                />
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-rajdhani text-gray-400 uppercase mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl px-4 py-3 text-white font-orbitron focus:border-t-blue-400/70 focus:border-l-blue-400/70 focus:border-r-blue-600/80 focus:border-b-blue-700/80 outline-none transition-all"
                  style={{
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                  }}
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-rajdhani text-gray-400 uppercase mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl px-4 py-3 text-white font-orbitron focus:border-t-blue-400/70 focus:border-l-blue-400/70 focus:border-r-blue-600/80 focus:border-b-blue-700/80 outline-none transition-all"
                  style={{
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                  }}
                  placeholder="Confirm new password"
                />
              </div>

              {/* Error Message */}
              {passwordError && (
                <div 
                  className="bg-gradient-to-b from-red-950/30 via-red-900/20 to-red-950/30 backdrop-blur-sm border-t-2 border-l-2 border-red-900/60 border-r-2 border-b-2 border-r-red-950/80 border-b-red-950/80 rounded-3xl p-3 text-red-400 text-sm font-rajdhani"
                  style={{
                    boxShadow: '0 6px 18px rgba(153, 27, 27, 0.5), 0 2px 6px rgba(153, 27, 27, 0.3), inset 0 1px 3px rgba(255, 100, 100, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  {passwordError}
                </div>
              )}

              {/* Security Note */}
              <div 
                className="bg-gradient-to-b from-blue-950/30 via-blue-900/20 to-blue-950/30 backdrop-blur-sm border-t-2 border-l-2 border-blue-900/60 border-r-2 border-b-2 border-r-blue-950/80 border-b-blue-950/80 rounded-3xl p-3"
                style={{
                  boxShadow: '0 6px 18px rgba(30, 58, 138, 0.5), 0 2px 6px rgba(30, 58, 138, 0.3), inset 0 1px 3px rgba(100, 150, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                }}
              >
                <p className="text-xs text-system-blue font-rajdhani">
                  🔐 <strong>Security Tip:</strong> Use a strong password with at least 6 characters, including numbers and special characters.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-2 mb-3 px-2">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordError('');
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="w-full sm:flex-1 px-4 py-3 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl text-white font-orbitron font-bold transition-all touch-manipulation hover:scale-105 active:scale-95"
                style={{
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={passwordChanging}
                className="w-full sm:flex-1 px-4 py-3 bg-gradient-to-b from-blue-800/40 via-blue-900/40 to-blue-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/60 border-r-2 border-b-2 border-r-blue-700/80 border-b-blue-700/80 rounded-3xl text-white font-orbitron font-bold tracking-wider transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 touch-manipulation"
                style={{
                  boxShadow: '0 6px 18px rgba(59, 130, 246, 0.5), 0 2px 6px rgba(59, 130, 246, 0.3), inset 0 1px 3px rgba(100, 150, 255, 0.08), inset 0 -2px 6px rgba(30, 58, 138, 0.4)'
                }}
              >
                {passwordChanging ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center p-3 sm:p-4 z-[9999] animate-fade-in modal-backdrop">
          <div 
            className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-800/70 rounded-3xl shadow-2xl max-w-sm sm:max-w-lg w-full max-h-[80vh] sm:max-h-[85vh] overflow-y-auto animate-scale-in"
            style={{
              boxShadow: '0 8px 24px rgba(239, 68, 68, 0.6), 0 2px 8px rgba(239, 68, 68, 0.4), inset 0 2px 4px rgba(255, 100, 100, 0.1), inset 0 -3px 8px rgba(153, 27, 27, 0.5)'
            }}
          >
            {/* Warning Header */}
            <div className="sticky top-0 border-b border-b-slate-600/30 p-3 sm:p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-orbitron font-black text-red-500 uppercase tracking-wider">
                  Delete Account
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteError('');
                  setDeleteConfirmEmail('');
                  setDeleteConfirmPassword('');
                }}
                disabled={isDeleting}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <span className="text-lg text-gray-400">✕</span>
              </button>
            </div>

            {/* Warning Message */}
            <div className="p-2 sm:p-3">
            <div 
              className="bg-gradient-to-b from-red-950/30 via-red-900/20 to-red-950/30 backdrop-blur-sm border-t-2 border-l-2 border-red-900/60 border-r-2 border-b-2 border-r-red-950/80 border-b-red-950/80 rounded-3xl p-4 mb-4"
              style={{
                boxShadow: '0 6px 18px rgba(153, 27, 27, 0.5), 0 2px 6px rgba(153, 27, 27, 0.3), inset 0 1px 3px rgba(255, 100, 100, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
              }}
            >
              <p className="text-red-400 font-rajdhani font-bold mb-2">
                ⚠️ THIS ACTION CANNOT BE UNDONE!
              </p>
              <p className="text-sm text-gray-300 font-rajdhani">
                Deleting your account will permanently remove:
              </p>
              <ul className="text-xs text-gray-400 font-rajdhani mt-2 space-y-1 ml-4">
                <li>• All your quest data and progress</li>
                <li>• Your level, stats, and achievements</li>
                <li>• All programs and passive skills</li>
                <li>• Your account and login credentials</li>
              </ul>
            </div>

            <div className="space-y-4">
              {/* Email Confirmation */}
              <div>
                <label className="block text-sm font-rajdhani text-gray-400 uppercase mb-2">
                  Confirm Your Email
                </label>
                <input
                  type="email"
                  value={deleteConfirmEmail}
                  onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                  disabled={isDeleting}
                  className="w-full bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl px-4 py-3 text-white font-orbitron focus:border-t-red-400/70 focus:border-l-red-400/70 focus:border-r-red-600/80 focus:border-b-red-700/80 outline-none transition-all disabled:opacity-50"
                  style={{
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                  }}
                  placeholder="Enter your email address"
                />
                <p className="text-xs text-gray-500 font-rajdhani mt-2">
                  Type your email address to confirm account deletion
                </p>
              </div>

              {/* Password Confirmation (only for email/password users) */}
              {user && user.providerData[0]?.providerId === 'password' && (
                <div>
                  <label className="block text-sm font-rajdhani text-gray-400 uppercase mb-2">
                    Confirm Your Password
                  </label>
                  <input
                    type="password"
                    value={deleteConfirmPassword}
                    onChange={(e) => setDeleteConfirmPassword(e.target.value)}
                    disabled={isDeleting}
                    className="w-full bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl px-4 py-3 text-white font-orbitron focus:border-t-red-400/70 focus:border-l-red-400/70 focus:border-r-red-600/80 focus:border-b-red-700/80 outline-none transition-all disabled:opacity-50"
                    style={{
                      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                    }}
                    placeholder="Enter your password"
                  />
                  <p className="text-xs text-gray-500 font-rajdhani mt-2">
                    Enter your password to verify it's really you
                  </p>
                </div>
              )}

              {/* Error Message */}
              {deleteError && (
                <div 
                  className="bg-gradient-to-b from-red-950/30 via-red-900/20 to-red-950/30 backdrop-blur-sm border-t-2 border-l-2 border-red-900/60 border-r-2 border-b-2 border-r-red-950/80 border-b-red-950/80 rounded-3xl p-3 text-red-400 text-sm font-rajdhani"
                  style={{
                    boxShadow: '0 6px 18px rgba(153, 27, 27, 0.5), 0 2px 6px rgba(153, 27, 27, 0.3), inset 0 1px 3px rgba(255, 100, 100, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  {deleteError}
                </div>
              )}

              {/* Final Warning */}
              <div 
                className="bg-gradient-to-b from-yellow-950/30 via-yellow-900/20 to-yellow-950/30 backdrop-blur-sm border-t-2 border-l-2 border-yellow-900/60 border-r-2 border-b-2 border-r-yellow-950/80 border-b-yellow-950/80 rounded-3xl p-3"
                style={{
                  boxShadow: '0 6px 18px rgba(161, 98, 7, 0.5), 0 2px 6px rgba(161, 98, 7, 0.3), inset 0 1px 3px rgba(255, 200, 100, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                }}
              >
                <p className="text-xs text-yellow-400 font-rajdhani">
                  💡 <strong>Before you go:</strong> Consider exporting your data first. Once deleted, there is no way to recover your account or progress.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-3 mb-4 px-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteError('');
                  setDeleteConfirmEmail('');
                  setDeleteConfirmPassword('');
                }}
                disabled={isDeleting}
                className="w-full sm:flex-1 px-4 py-3 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl text-white font-orbitron font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation hover:scale-105 active:scale-95"
                style={{
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={
                  isDeleting ||
                  !(
                    deleteConfirmEmail &&
                    deleteConfirmEmail.toLowerCase() === (user?.email?.toLowerCase() || '') &&
                    (user && user.providerData[0]?.providerId === 'password' ? !!deleteConfirmPassword : true)
                  )
                }
                className={`w-full sm:flex-1 px-4 py-3 rounded-3xl font-orbitron font-bold tracking-wider transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 touch-manipulation border-t-2 border-l-2 border-r-2 border-b-2 ${
                  deleteConfirmEmail &&
                  deleteConfirmEmail.toLowerCase() === (user?.email?.toLowerCase() || '') &&
                  (user && user.providerData[0]?.providerId === 'password' ? !!deleteConfirmPassword : true)
                    ? 'bg-gradient-to-b from-red-800/40 via-red-900/40 to-red-950/40 backdrop-blur-sm border-red-500/60 border-r-red-700/80 border-b-red-700/80'
                    : 'bg-gradient-to-b from-red-900/20 via-red-950/20 to-red-950/20 backdrop-blur-sm border-red-900/40 border-r-red-950/50 border-b-red-950/50 cursor-not-allowed'
                }`}
                style={{
                  boxShadow: '0 6px 18px rgba(239, 68, 68, 0.5), 0 2px 6px rgba(239, 68, 68, 0.3), inset 0 1px 3px rgba(255, 100, 100, 0.08), inset 0 -2px 6px rgba(153, 27, 27, 0.4)'
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Background Customizer Modal */}
      {showBackgroundCustomizer && (user || isDemoMode) && profile && (
        <BackgroundCustomizer
          currentSettings={getBackgroundSettings()}
          onSave={updateBackgroundSettings}
          onClose={() => {
            setShowBackgroundCustomizer(false);
            setBackgroundInitialType(undefined);
            setBackgroundInitialParticle(undefined);
          }}
          userId={user?.uid || 'demo_user'}
          userProfile={profile}
          initialType={backgroundInitialType}
          initialParticleEffect={backgroundInitialParticle}
          onShowSubscription={() => {
            setShowBackgroundCustomizer(false);
            if (isDemoMode) {
              // In demo mode, show signup notification instead of subscription modal
              alert('🔒 Sign up to unlock custom background uploads and save your progress!');
            } else {
              setShowSubscriptionModal(true);
            }
          }}
        />
      )}
      
      {/* Subscription Modal */}
      {profile && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          currentTier={profile.subscription.tier}
          subscriptionStatus={profile.subscription.status}
          trialDaysRemaining={subscriptionService.getTrialDaysRemaining(profile.subscription)}
        />
      )}
    </div>
  );
};

export default SettingsPageNew;

